import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Stack,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const UsuariosDialog = ({
  openUsuarioDialog,
  handleCloseUsuarioDialog,
  isMobile,
  isConnected,
  idFormularioActual,
  selectedFormulario,
  usuariosDisponibles,
  formulariosEspecificos,
  setFormulariosEspecificos,
  enviandoCredenciales,
  editingUsuario,
  fechasEdicion,
  setFechasEdicion,
  agregarUsuario,
  eliminarUsuario,
  enviarCredencialesIndividual,
  toggleEdicion,
  guardarFechasEdicion,
  formatDate,
  determinarAccionesUsuario,
  onRefresh,
  isRefreshing,
  onCredencialesEnviadas = () => { }
}) => {
  const [searchText, setSearchText] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('TODAS');
  const [filtroAsignado, setFiltroAsignado] = useState('TODOS');
  const [filtroNotificado, setFiltroNotificado] = useState('TODOS');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLoading, setIsLoading] = useState(true);

  // Efecto para controlar el estado de carga cuando se abra el diálogo o cambien los datos
  React.useEffect(() => {
    if (openUsuarioDialog) {
      setIsLoading(true);
      // Pequeño delay para asegurar que el diálogo se abra antes de mostrar el spinner
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [openUsuarioDialog]);

  // Resetear carga cuando cambien los datos principales
  React.useEffect(() => {
    if (openUsuarioDialog && !isRefreshing) {
      setIsLoading(false);
    }
  }, [usuariosDisponibles, openUsuarioDialog, isRefreshing]);

  const proveedores = useMemo(() => {
    return usuariosDisponibles.filter(u => u.rol === 'proveedor');
  }, [usuariosDisponibles]);

  const handleFechasChange = useCallback((field, value) => {
    if (!value) {
      setFechasEdicion(prev => ({ ...prev, [field]: null }));
      return;
    }

    const [year, month, day] = value.split('-');
    const fechaLocal = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    setFechasEdicion(prev => ({ ...prev, [field]: fechaLocal }));
  }, [setFechasEdicion]);

  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setTipoFiltro('TODAS');
    setFiltroAsignado('TODOS');
    setFiltroNotificado('TODOS');
    setPage(0);
  }, []);

  const usuariosFiltrados = useMemo(() => {
    return proveedores.filter((u) => {
      const searchLower = searchText.toLowerCase();
      const coincideTexto =
        u.nombre?.toLowerCase().includes(searchLower) ||
        u.usuario?.toLowerCase().includes(searchLower) ||
        u.correo?.toLowerCase().includes(searchLower);

      const coincideTipo = tipoFiltro === 'TODAS' || u.tipo_persona === tipoFiltro;

      const tieneFormulario = formulariosEspecificos[u.id_usuario] != null;
      const coincideAsignado =
        filtroAsignado === 'TODOS' ||
        (filtroAsignado === 'ASIGNADO' && tieneFormulario) ||
        (filtroAsignado === 'NO_ASIGNADO' && !tieneFormulario);

      const notificadoCredenciales = formulariosEspecificos[u.id_usuario]?.notificado_credenciales || false;
      const coincideNotificado =
        filtroNotificado === 'TODOS' ||
        (filtroNotificado === 'NOTIFICADO' && notificadoCredenciales) ||
        (filtroNotificado === 'NO_NOTIFICADO' && tieneFormulario && !notificadoCredenciales);

      return coincideTexto && coincideTipo && coincideAsignado && coincideNotificado;
    });
  }, [proveedores, searchText, tipoFiltro, filtroAsignado, filtroNotificado, formulariosEspecificos]);

  const paginatedUsuarios = useMemo(() => {
    const start = page * rowsPerPage;
    return usuariosFiltrados.slice(start, start + rowsPerPage);
  }, [usuariosFiltrados, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEnviarCredenciales = async (usuario) => {
    const formUsuario = formulariosEspecificos[usuario.id_usuario];

    if (!formUsuario) {
      setSnackbar({
        open: true,
        message: 'El usuario no tiene un formulario asignado',
        severity: 'warning'
      });
      return;
    }

    try {
      const result = await enviarCredencialesIndividual(usuario);

      if (result) {
        setFormulariosEspecificos(prev => ({
          ...prev,
          [usuario.id_usuario]: {
            ...prev[usuario.id_usuario],
            notificado_credenciales: true
          }
        }));

        setSnackbar({
          open: true,
          message: `Credenciales enviadas exitosamente a ${usuario.nombre}`,
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al enviar las credenciales',
        severity: 'error'
      });
    }
  };

  const formatFechaDB = (fechaStr) => {
    if (!fechaStr) return 'Sin fecha';

    if (typeof fechaStr === 'string') {
      const datePart = fechaStr.split('T')[0];
      const [year, month, day] = datePart.split('-');

      if (year && month && day) {
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
    }

    if (fechaStr instanceof Date && !isNaN(fechaStr.getTime())) {
      const year = fechaStr.getFullYear();
      const month = (fechaStr.getMonth() + 1).toString().padStart(2, '0');
      const day = fechaStr.getDate().toString().padStart(2, '0');
      return `${day}/${month}/${year}`;
    }

    return 'Sin fecha';
  };

  const formatFechaDisplay = (fecha) => {
    if (!fecha) return 'Sin fecha';

    try {
      let dateObj;

      if (typeof fecha === 'string') {
        const datePart = fecha.split('T')[0];
        const [year, month, day] = datePart.split('-');
        if (year && month && day) {
          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }
      }

      if (fecha instanceof Date && !isNaN(fecha.getTime())) {
        const year = fecha.getFullYear();
        const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
        const day = fecha.getDate().toString().padStart(2, '0');
        return `${day}/${month}/${year}`;
      }

      return 'Sin fecha';
    } catch {
      return 'Sin fecha';
    }
  };

  const formatFechaInput = (fecha) => {
    if (!fecha) return '';

    try {
      if (typeof fecha === 'string') {
        const datePart = fecha.split('T')[0];
        return datePart;
      }

      if (fecha instanceof Date && !isNaN(fecha.getTime())) {
        const year = fecha.getFullYear();
        const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
        const day = fecha.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      return '';
    } catch {
      return '';
    }
  };

  const parseFechaLocal = (fechaStr) => {
    if (!fechaStr) return null;

    try {
      if (typeof fechaStr === 'string') {
        const datePart = fechaStr.split('T')[0];
        const [year, month, day] = datePart.split('-');
        if (year && month && day) {
          return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        }
      }

      if (fechaStr instanceof Date && !isNaN(fechaStr.getTime())) {
        return new Date(fechaStr.getFullYear(), fechaStr.getMonth(), fechaStr.getDate());
      }

      return null;
    } catch {
      return null;
    }
  };


  const getFormularioStatus = (formulario) => {
    if (!formulario) return 'No disponible';

    if (formulario.estado === 'inactivo') return 'Inactivo';

    const ahora = new Date();
    const getLocalDate = (fechaStr) => {
      if (!fechaStr) return null;
      const datePart = typeof fechaStr === 'string' ? fechaStr.split('T')[0] : fechaStr;
      if (typeof datePart === 'string' && datePart.includes('-')) {
        const [year, month, day] = datePart.split('-');
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      }
      return new Date(datePart);
    };

    const fechaInicio = getLocalDate(formulario.fecha_inicio);
    const fechaFin = getLocalDate(formulario.fecha_fin);

    if (fechaFin && ahora > fechaFin) return 'Expirado';
    if (fechaInicio && ahora < fechaInicio) return 'Pendiente';
    return formulario.estado === 'activo' ? 'Activo' : formulario.estado || 'Activo';
  };

  const getSelectedFormularioStatus = () => getFormularioStatus(selectedFormulario);

  const getSelectedFormularioDescription = () => {
    if (!selectedFormulario || !selectedFormulario.descripcion) return 'Sin descripción';
    return selectedFormulario.descripcion;
  };

  const getSelectedFormularioName = () => selectedFormulario?.titulo || 'Formulario no seleccionado';

  const getSelectedFormularioStart = () => formatFechaDB(selectedFormulario?.fecha_inicio);

  const getSelectedFormularioEnd = () => formatFechaDB(selectedFormulario?.fecha_fin);

  const getSelectedFormularioStatusLabel = () => getSelectedFormularioStatus();

  const getSelectedFormularioInfo = () => {
    return (
      <Box sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fbfbfb' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Fecha de inicio:&nbsp;</strong>{getSelectedFormularioStart()}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Fecha de fin:&nbsp;</strong>{getSelectedFormularioEnd()}
            </Typography>
          </Box>

          <Box sx={{ flex: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Nombre:&nbsp;</strong>{getSelectedFormularioName()}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Descripción:&nbsp;</strong>{getSelectedFormularioDescription()}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const getButtonText = (usuario) => {
    const formUsuario = formulariosEspecificos[usuario.id_usuario];
    const acciones = determinarAccionesUsuario(usuario);

    if (acciones.mostrarReasignar) return 'Reasignar';
    if (acciones.mostrarAgregar) {
      if (formUsuario && formUsuario.visible === 0 && formUsuario.estado !== 'finalizado') return 'Reactivar';
      return 'Agregar';
    }
    return '';
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      setIsLoading(true);
      await onRefresh();
      setPage(0);
      // Pequeño delay para asegurar que los datos se actualizaron
      setTimeout(() => setIsLoading(false), 300);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getDiasParaExpirar = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return { texto: '-', color: 'default' };

    const getLocalDate = (fechaStr) => {
      const datePart = typeof fechaStr === 'string' ? fechaStr.split('T')[0] : fechaStr;
      if (typeof datePart === 'string' && datePart.includes('-')) {
        const [year, month, day] = datePart.split('-');
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      }
      return new Date(datePart);
    };

    const inicio = getLocalDate(fechaInicio);
    const fin = getLocalDate(fechaFin);
    const actual = new Date();
    actual.setHours(0, 0, 0, 0);

    if (!inicio || !fin) return { texto: '-', color: 'default' };

    inicio.setHours(0, 0, 0, 0);
    fin.setHours(0, 0, 0, 0);

    if (actual > fin) {
      return { texto: 'Expirado', color: 'error' };
    }

    if (actual < inicio) {
      const diffTime = inicio.getTime() - actual.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return { texto: `Inicia en ${diffDays} días`, color: 'warning' };
    }

    const diffTime = fin.getTime() - actual.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { texto: `Expira en ${diffDays} días`, color: 'success' };
  };

  // Determinar si estamos cargando (mostrar spinner)
  const showLoading = isLoading || isRefreshing || proveedores.length === 0 && !isLoading && !isRefreshing && openUsuarioDialog;

  return (
    <>
      <Dialog
        open={openUsuarioDialog}
        onClose={handleCloseUsuarioDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">
                Agregar proveedores al formulario
              </Typography>
              {selectedFormulario && (
                <Chip
                  label={getSelectedFormularioStatusLabel()}
                  color={
                    getSelectedFormularioStatusLabel() === 'Activo' ? 'success' :
                      getSelectedFormularioStatusLabel() === 'Expirado' ? 'error' :
                        getSelectedFormularioStatusLabel() === 'Pendiente' ? 'warning' : 'default'
                  }
                  size="small"
                />
              )}
              {isConnected && (
                <Chip label="En tiempo real" color="success" size="small" />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Actualizar datos">
                <IconButton
                  onClick={handleRefresh}
                  color="primary"
                  size="small"
                  disabled={isRefreshing || isLoading}
                >
                  {isRefreshing || isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {getSelectedFormularioInfo()}

          {/* Mostrar spinner mientras se carga */}
          {showLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
                py: 8
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Cargando proveedores...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, borderRadius: 1, mb: 3 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Buscar por usuario, nombre o correo"
                    variant="outlined"
                    size="small"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchText && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchText('')}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                      <InputLabel>Tipo de persona</InputLabel>
                      <Select
                        value={tipoFiltro}
                        onChange={(e) => setTipoFiltro(e.target.value)}
                        label="Tipo de persona"
                      >
                        <MenuItem value="TODAS">Todos los tipos</MenuItem>
                        <MenuItem value="natural">Persona Natural</MenuItem>
                        <MenuItem value="juridica">Persona Jurídica</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                      <InputLabel>Estado de asignación</InputLabel>
                      <Select
                        value={filtroAsignado}
                        onChange={(e) => setFiltroAsignado(e.target.value)}
                        label="Estado de asignación"
                      >
                        <MenuItem value="TODOS">Todos</MenuItem>
                        <MenuItem value="ASIGNADO">Asignados</MenuItem>
                        <MenuItem value="NO_ASIGNADO">No asignados</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                      <InputLabel>Estado credenciales</InputLabel>
                      <Select
                        value={filtroNotificado}
                        onChange={(e) => setFiltroNotificado(e.target.value)}
                        label="Estado credenciales"
                      >
                        <MenuItem value="TODOS">Todos</MenuItem>
                        <MenuItem value="NOTIFICADO">Credenciales enviadas</MenuItem>
                        <MenuItem value="NO_NOTIFICADO">Credenciales no enviadas</MenuItem>
                      </Select>
                    </FormControl>

                    {(searchText || tipoFiltro !== 'TODAS' || filtroAsignado !== 'TODOS' || filtroNotificado !== 'TODOS') && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleClearFilters}
                        startIcon={<ClearIcon />}
                        sx={{ minWidth: '120px' }}
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Box
                mb={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Mostrando <strong>{usuariosFiltrados.length}</strong> de{" "}
                  <strong>{proveedores.length}</strong> proveedores
                </Typography>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={usuariosFiltrados.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas"
                  sx={{
                    "& .MuiTablePagination-toolbar": {
                      minHeight: "auto",
                      padding: 0,
                    },
                  }}
                />
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f0f0f0' }}>
                    <TableRow>
                      <TableCell><strong>N°</strong></TableCell>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell><strong>Días para expirar</strong></TableCell>
                      <TableCell><strong>Correo</strong></TableCell>
                      <TableCell><strong>Tipo</strong></TableCell>
                      <TableCell><strong>Fechas</strong></TableCell>
                      <TableCell align="center"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {console.log(formulariosEspecificos)}
                    {paginatedUsuarios.length > 0 ? (
                      paginatedUsuarios.map((usuario, index) => {
                        const formUsuario = formulariosEspecificos[usuario.id_usuario];
                        const fechaInicio = formUsuario ? formUsuario.fecha_inicio : null;
                        const fechaFin = formUsuario ? formUsuario.fecha_fin : null;
                        const acciones = determinarAccionesUsuario(usuario);
                        const notificadoCredenciales = formUsuario?.notificado_credenciales || false;
                        return (
                          <TableRow key={usuario.id_usuario} hover>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>{usuario.nombre}</TableCell>
                            <TableCell>
                              {formUsuario
                                ? (() => {
                                  const expData = getDiasParaExpirar(fechaInicio, fechaFin);
                                  return <Chip size="small" label={expData.texto} variant="outlined" color={expData.color} />;
                                })()
                                : <Typography variant="caption" color="text.secondary">Sin asignar</Typography>
                              }
                            </TableCell>
                            <TableCell>{usuario.correo}</TableCell>
                            <TableCell>
                              <Chip
                                label={usuario.tipo_persona === 'natural' ? 'Natural' : 'Jurídica'}
                                size="small"
                                color={usuario.tipo_persona === 'natural' ? 'primary' : 'secondary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {formUsuario && acciones.mostrarFechas ? (
                                <Box>
                                  <Typography variant="caption" display="block">
                                    Inicio: {formatFechaDisplay(fechaInicio)}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Fin: {formatFechaDisplay(fechaFin)}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {formUsuario && formUsuario.estado === 'finalizado' ? 'Formulario finalizado' : 'Sin asignar'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                                {(acciones.mostrarAgregar || acciones.mostrarReasignar) && (
                                  <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    onClick={() => agregarUsuario(usuario.id_usuario)}
                                    sx={{ minWidth: 80 }}
                                  >
                                    {getButtonText(usuario)}
                                  </Button>
                                )}

                                {acciones.mostrarEditar && acciones.mostrarQuitar && acciones.mostrarEnviarCredenciales && formUsuario && (
                                  <>
                                    <Tooltip title={notificadoCredenciales ? 'Credenciales ya enviadas' : 'Enviar credenciales'}>
                                      <span>
                                        <Button
                                          size="small"
                                          variant={notificadoCredenciales ? 'contained' : 'outlined'}
                                          color={notificadoCredenciales ? 'success' : 'primary'}
                                          onClick={() => handleEnviarCredenciales(usuario)}
                                          disabled={enviandoCredenciales[usuario.id_usuario]}
                                          startIcon={
                                            enviandoCredenciales[usuario.id_usuario]
                                              ? <CircularProgress size={16} color="inherit" />
                                              : notificadoCredenciales
                                                ? <i className="bi bi-envelope-check-fill" />
                                                : <EmailIcon fontSize="small" />
                                          }
                                          sx={{ minWidth: 40 }}
                                        >
                                        </Button>
                                      </span>
                                    </Tooltip>

                                    <Tooltip title="Editar fechas">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => toggleEdicion(usuario)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>

                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      onClick={() => eliminarUsuario(usuario.id_usuario)}
                                      disabled={enviandoCredenciales[usuario.id_usuario]}
                                      sx={{ minWidth: 60 }}
                                    >
                                      Quitar
                                    </Button>
                                  </>
                                )}
                              </Box>

                              {editingUsuario === usuario.id_usuario && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                  <Stack spacing={1}>
                                    <TextField
                                      label="Inicio"
                                      type="date"
                                      size="small"
                                      value={fechasEdicion.fecha_inicio ? formatFechaInput(fechasEdicion.fecha_inicio) : ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value) {
                                          const [year, month, day] = value.split('-');
                                          const fechaLocal = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
                                          setFechasEdicion(prev => ({ ...prev, fecha_inicio: fechaLocal }));
                                        } else {
                                          setFechasEdicion(prev => ({ ...prev, fecha_inicio: null }));
                                        }
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      fullWidth
                                    />
                                    <TextField
                                      label="Fin"
                                      type="date"
                                      size="small"
                                      value={fechasEdicion.fecha_fin ? formatFechaInput(fechasEdicion.fecha_fin) : ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value) {
                                          const [year, month, day] = value.split('-');
                                          const fechaLocal = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
                                          setFechasEdicion(prev => ({ ...prev, fecha_fin: fechaLocal }));
                                        } else {
                                          setFechasEdicion(prev => ({ ...prev, fecha_fin: null }));
                                        }
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      fullWidth
                                    />
                                    <Box display="flex" gap={0.5}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => guardarFechasEdicion(formUsuario?.id_formulario, usuario)}
                                        fullWidth
                                      >
                                        Guardar
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => toggleEdicion(usuario)}
                                        fullWidth
                                      >
                                        Cancelar
                                      </Button>
                                    </Box>
                                  </Stack>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary" gutterBottom>
                            No se encontraron proveedores
                          </Typography>
                          {(searchText || tipoFiltro !== 'TODAS' || filtroAsignado !== 'TODOS' || filtroNotificado !== 'TODOS') && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleClearFilters}
                            >
                              Limpiar filtros
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {!showLoading && `${usuariosFiltrados.length} proveedores encontrados`}
            </Typography>
            <Button onClick={handleCloseUsuarioDialog} variant="contained">
              Cerrar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UsuariosDialog;